import { z } from 'zod/v3';

export const cdepStatusResultSchema = z.object({
  currentVersions: z
    .record(
      z.string().describe('The release group.'),
      z.object({
        commits: z
          .array(
            z.object({
              sha: z.string(),
              url: z.string(),
            }),
          )
          .describe(
            'The list of appfolio/apm_bundle commit SHAs that are included in this release group.',
          ),
      }),
    )
    .describe('The current deployed release groups.'),
  lastRelease: z.object({
    completed: z
      .string()
      .datetime({ offset: true })
      .describe(
        'The date and time, in RFC3339Nano format, that the last release completed.',
      ),
    pullRequests: z
      .array(
        z.object({
          id: z.string(),
          url: z.string(),
        }),
      )
      .describe(
        'The GitHub pull request IDs that were included in this release.',
      ),
    releaseGroups: z
      .array(z.string())
      .describe('The release groups that were part of the last release.'),
    commit: z
      .object({
        sha: z.string(),
        url: z.string(),
      })
      .describe('The appfolio/apm_bundle commit SHA that was deployed.'),
  }),
  nextRelease: z.object({
    expected: z
      .string()
      .datetime({ offset: true })
      .optional()
      .describe(
        'The date and time, in RFC3339Nano format, that the next release is expected to complete. Omitted when there is no meaningful estimate, notably while CD is paused.',
      ),
    commit: z
      .object({
        sha: z.string(),
        url: z.string(),
      })
      .optional()
      .describe(
        'The appfolio/apm_bundle commit SHA that will be deployed with this release.',
      ),
    pullRequests: z
      .array(
        z.object({
          id: z.string(),
          url: z.string(),
        }),
      )
      .optional()
      .describe(
        'The GitHub pull request IDs that are included in this release.',
      ),
  }),
  status: z.object({
    description: z
      .string()
      .describe('The current status of the continuous deployment system.'),
    code: z
      .union([
        z.enum([
          'running',
          'not_running',
          'blocked',
          'release_group_paused',
          'failed',
          'interrupted',
          'cancelled',
        ]),
        z.string(),
      ])
      .describe(
        'The continuous deployment system status code. Known values are running, not_running, blocked, release_group_paused, failed, interrupted, and cancelled; the API may add new ones.',
      ),
    isRunning: z
      .boolean()
      .describe(
        'Whether the continuous deployment system is currently running.',
      ),
    blockReason: z
      .string()
      .optional()
      .describe(
        'The reason the continuous deployment system is blocked from running.',
      ),
    haltedReleaseGroup: z
      .string()
      .optional()
      .describe(
        'The release group CD is stopped at when code is blocked, failed, interrupted, or cancelled. Matches a key in currentVersions; the group and every group after it in release order cannot advance.',
      ),
    haltedStage: z
      .string()
      .optional()
      .describe(
        'The stage that stopped CD (e.g. burn-in, smoke test), present when code is failed or interrupted.',
      ),
  }),
  schedule: z
    .object({
      paused: z
        .boolean()
        .describe('Whether the whole release schedule is paused.'),
      note: z
        .string()
        .optional()
        .describe(
          'The note recorded when the schedule was last paused or resumed.',
        ),
      releaseGroups: z
        .array(z.string())
        .optional()
        .describe(
          'The configured release group names, in release order. Absent when the release-group pause state could not be read, in which case pausedReleaseGroups is unknown rather than empty.',
        ),
      pausedReleaseGroups: z
        .array(
          z.object({
            name: z.string().describe('The paused release group.'),
            by: z.string().optional().describe('Who paused the release group.'),
            message: z
              .string()
              .optional()
              .describe('Why the release group was paused.'),
            at: z
              .string()
              .datetime({ offset: true })
              .optional()
              .describe(
                'The date and time, in RFC3339Nano format, that the release group was paused.',
              ),
          }),
        )
        .optional()
        .describe(
          'The individually paused release groups. A scheduled release stops at the first one in release order.',
        ),
    })
    .optional()
    .describe('The release schedule pause state.'),
  updated: z
    .string()
    .datetime({ offset: true })
    .describe(
      'The date and time, in RFC3339Nano format, that this data was last updated.',
    ),
});
export type CdepStatusResult = z.infer<typeof cdepStatusResultSchema>;

export const cdepPullRequestResultSchema = z.object({
  id: z.string().describe('The pull request ID'),
  status: z
    .union([
      z.enum(['not_releasing', 'releasing', 'partially_released', 'released']),
      z.string(),
    ])
    .describe(
      "The pull request's release status. Known values are not_releasing, releasing, partially_released, and released; the API may add new ones.",
    ),
  expectedReleaseCompletion: z
    .string()
    .datetime({ offset: true })
    .describe(
      'The date and time, in RFC3339Nano format, of estimated pull request release completion.',
    )
    .optional(),
  releases: z
    .record(
      z.string().describe('The release group'),
      z
        .string()
        .datetime({ offset: true })
        .describe(
          'The date and time, in RFC3339Nano format, that the pull request was released to the release group.',
        ),
    )
    .describe('The release groups that contain this pull request.')
    .optional(),
});
export type CdepPullRequestResult = z.infer<typeof cdepPullRequestResultSchema>;
